import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Package, MessageCircle, Trash2, CheckCheck, Loader2 } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';

interface Notification {
  _id: string;
  type: 'order' | 'offer' | 'bid' | 'product' | 'account' | 'promotional' | 'admin_message';
  title: string;
  message: string;
  body?: string;
  payload?: any; // New field name from backend
  data?: any; // Legacy field name for backward compatibility
  link?: string;
  channel?: 'websocket' | 'push' | 'email' | 'in-app';
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

const NotificationDropdown: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { 
    notifications: allNotifications, 
    isLoading,
    markAsRead, 
    deleteNotification,
    unreadCount
  } = useNotifications();
  
  // Orders and platform messages only — offers are buyer/seller negotiations
  const notifications = allNotifications.filter((n: Notification) => {
    const dataType = String((n.payload || n.data)?.type || '');
    const isOffer =
      n.type === 'bid' ||
      n.type === 'offer' ||
      dataType.includes('bid') ||
      dataType.includes('offer');
    if (isOffer) return false;
    return (
      n.type === 'order' ||
      n.type === 'admin_message' ||
      dataType.includes('order')
    );
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);


  // Format notification time
  const formatNotificationTime = (dateString: string) => {
    if (!dateString) return 'Just now';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMins = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMins < 1) return 'Just now';
    if (diffInMins < 60) return `${diffInMins}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get notification icon
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <Package size={18} />;
      case 'bid':
        return <MessageCircle size={18} />;
      default:
        return <Bell size={18} />;
    }
  };

  // Get navigation path based on notification type and data
  const getNotificationPath = (notification: Notification): string => {
    const data = notification.payload || notification.data || {};
    const notificationType = data.type || notification.type || '';
    
    // Order notifications - always go to orders page
    if (notification.type === 'order' || notificationType.includes('order') || notificationType === 'order_created' || notificationType === 'order_status_changed') {
      const orderId = data.orderId || notification.link?.split('orderId=')[1]?.split('&')[0];
      if (orderId) {
        return `/orders?orderId=${orderId}`;
      }
      return '/orders';
    }
    
  // Offer / bid notifications
    if (
      notification.type === 'bid' ||
      notificationType.includes('bid') ||
      notificationType.includes('offer') ||
      notificationType === 'bid_created' ||
      notificationType === 'bid_status_changed' ||
      notificationType === 'offer_created'
    ) {
      const offerId = data.offerId || data.bidId;
      const productId = data.productId;
      if (offerId) return `/offers/${offerId}`;
      if (productId) return `/offers/product/${productId}`;
      return '/offers';
    }
    
    // Product notifications - always go to products page
    if (notification.type === 'product' || notificationType.includes('product') || notificationType === 'product_created') {
      const productId = data.productId || notification.link?.split('productId=')[1]?.split('&')[0];
      if (productId) {
        return `/products?productId=${productId}`;
      }
      return '/products';
    }
    
    // Admin message notifications - check the type in data
    if (notification.type === 'admin_message') {
      if (notificationType.includes('order')) {
        return '/orders';
      } else if (notificationType.includes('bid') || notificationType.includes('offer')) {
        return '/offers';
      } else if (notificationType.includes('product')) {
        return '/products';
      }
    }
    
    // Default fallback
    return notification.link || '/dashboard';
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read and delete (so it disappears) - same as st_cael_website
    try {
      if (!notification.isRead && notification._id) {
        await markAsRead(notification._id);
      }
      // Delete the notification after marking as read so it doesn't appear anymore
      await deleteNotification(notification._id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }

    // Close dropdown
    setIsOpen(false);

    // Navigate to the appropriate page
    const path = getNotificationPath(notification);
    navigate(path);
  };

  const handleMarkAllAsRead = async () => {
    try {
      // Mark as read and delete all visible notifications (same as st_cael_website)
      const unreadNotifications = notifications.filter((n: Notification) => !n.isRead);
      await Promise.all(
        unreadNotifications.map(async (notification: Notification) => {
          await markAsRead(notification._id);
          await deleteNotification(notification._id);
        })
      );
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  className="p-1 text-gray-400 hover:text-gray-600"
                  onClick={handleMarkAllAsRead}
                  title="Mark all as read"
                >
                  <CheckCheck size={16} />
                </button>
              )}
              <button
                className="p-1 text-gray-400 hover:text-gray-600"
                onClick={() => setIsOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 size={24} className="mx-auto text-gray-400 mb-2 animate-spin" />
                <p className="text-gray-500">Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={48} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">No notifications</p>
                <span className="text-sm text-gray-400">You're all caught up!</span>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notifications.map((notification: Notification) => (
                  <div
                    key={notification._id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !notification.isRead ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-500 uppercase">
                            {notification.type}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatNotificationTime(notification.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                      </div>
                      <button
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500"
                        onClick={(e) => handleDeleteNotification(e, notification._id)}
                        title="Delete notification"
                      >
                        <Trash2 size={14} />
                      </button>
                      {!notification.isRead && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;

